import React, { useCallback, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ImageDown, CheckCircle2, AlertTriangle, Eye } from 'lucide-react';

/**
 * Super-admin dashboard for the cover_mirror_worker backlog. Shows how many
 * completed songs still have no cover (or a cover on an external CDN) and
 * lets the admin preview / run an on-demand repair sweep.
 */
export default function CoverMirrorCard({ API, nativeFetchWithAuth }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // 'dry' | 'apply' | null
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await nativeFetchWithAuth(`${API}/admin/cover-mirror/status`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStatus(await r.json());
    } catch (e) {
      toast.error(`Failed to load cover mirror status: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [API, nativeFetchWithAuth]);

  useEffect(() => { load(); }, [load]);

  const runSweep = async (dryRun) => {
    if (running) return;
    if (!dryRun && !window.confirm('Repair up to 25 covers now? Songs whose original cover can no longer be fetched will get an AI-generated cover (small Atlas cost each).')) return;
    setRunning(dryRun ? 'dry' : 'apply');
    const t = toast.loading(dryRun ? 'Previewing cover repairs…' : 'Repairing covers (this can take a minute)…');
    try {
      const r = await nativeFetchWithAuth(
        `${API}/admin/cover-mirror/sweep-now?limit=25&dry_run=${dryRun ? 'true' : 'false'}`,
        { method: 'POST', credentials: 'include' },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setReport(d);
      toast.dismiss(t);
      const s = d.summary || {};
      toast.success(
        dryRun
          ? `${s.attempted || 0} song(s) need a cover repair`
          : `Repaired ${s.repaired || 0} / ${s.attempted || 0} (${s.failed || 0} failed, ${s.generated || 0} generated)`,
        { duration: 8000 },
      );
      if (!dryRun) await load();
    } catch (e) {
      toast.dismiss(t);
      toast.error(`Cover sweep failed: ${e.message}`);
    } finally {
      setRunning(null);
    }
  };

  if (loading && !status) {
    return (
      <div className="glass-card p-4 flex items-center gap-3" data-testid="cover-mirror-loading">
        <Loader2 className="h-5 w-5 animate-spin text-white/60" />
        <span className="text-sm text-white/60">Loading cover mirror status…</span>
      </div>
    );
  }
  if (!status) return null;

  const pending = status.pending_total;

  return (
    <div className="glass-card p-5" data-testid="cover-mirror-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <ImageDown className="h-4 w-4 text-primary" />
            Cover Art Mirror to HP Server
          </h3>
          <p className="text-xs text-white/60 mt-1 max-w-xl">
            Every completed song should have its cover on the HP media server. A background worker
            repairs songs whose cover is missing or still on MusicGPT / SUNO CDN — re-fetching from
            the provider, pulling art embedded in the MP3, or generating one as a last resort.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline" size="sm" onClick={load} disabled={loading}
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="cover-mirror-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline" size="sm" onClick={() => runSweep(true)} disabled={!!running || pending === 0}
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="cover-mirror-preview-btn"
          >
            {running === 'dry' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
            Preview
          </Button>
          <Button
            size="sm" onClick={() => runSweep(false)} disabled={!!running || pending === 0}
            className="bg-purple-600 hover:bg-purple-500 text-white"
            data-testid="cover-mirror-sweep-btn"
          >
            {running === 'apply'
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Repairing…</>
              : <><ImageDown className="h-4 w-4 mr-1" /> Repair 25 now</>}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox testid="cover-stat-missing" label="No cover" value={status.missing_total}
          tone={status.missing_total > 0 ? 'warning' : 'ok'} />
        <StatBox testid="cover-stat-external" label="On external CDN" value={status.external_total}
          tone={status.external_total > 0 ? 'warning' : 'ok'} />
        <StatBox testid="cover-stat-hp" label="On HP server" value={status.already_on_hp} tone="ok"
          icon={<CheckCircle2 className="h-3 w-3 text-emerald-400" />} />
        <StatBox testid="cover-stat-giveup" label="Given up" value={status.given_up}
          tone={status.given_up > 0 ? 'danger' : 'ok'}
          icon={status.given_up > 0 ? <AlertTriangle className="h-3 w-3 text-amber-400" /> : null} />
      </div>

      {status.pending_by_host && Object.keys(status.pending_by_host).length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="text-xs text-white/60 mb-2">External covers by host:</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(status.pending_by_host).map(([host, n]) => (
              <span key={host} className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/80">
                <span className="font-mono">{host}</span> · {n}
              </span>
            ))}
          </div>
        </div>
      )}

      {report && <SweepReport report={report} />}
    </div>
  );
}

function SweepReport({ report }) {
  const rows = report.results || [];
  const tone = {
    repaired: 'text-emerald-300',
    planned: 'text-sky-300',
    skipped: 'text-white/50',
    failed: 'text-red-300',
  };
  return (
    <div className="mt-4 pt-4 border-t border-white/10" data-testid="cover-sweep-report">
      <div className="text-xs text-white/60 mb-2">
        {report.dry_run ? 'Preview — nothing was changed.' : 'Last repair run'} · {rows.length} song(s)
      </div>
      <div className="max-h-72 overflow-y-auto rounded-lg border border-white/10">
        <table className="w-full text-xs">
          <thead className="bg-white/5 text-white/60 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Song</th>
              <th className="text-left px-3 py-2 font-medium">Engine</th>
              <th className="text-left px-3 py-2 font-medium">Outcome</th>
              <th className="text-left px-3 py-2 font-medium">{report.dry_run ? 'Strategy chain' : 'Source / error'}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.song_id} className="border-t border-white/5" data-testid={`cover-sweep-row-${r.song_id}`}>
                <td className="px-3 py-2 text-white/90">
                  <div className="truncate max-w-[220px]" title={r.title || r.song_id}>{r.title || '—'}</div>
                  <div className="font-mono text-[10px] text-white/40">{r.song_id}</div>
                </td>
                <td className="px-3 py-2 text-white/70">{r.engine}</td>
                <td className={`px-3 py-2 font-medium ${tone[r.outcome] || 'text-white/70'}`}>{r.outcome}</td>
                <td className="px-3 py-2 text-white/70 break-words">
                  {r.outcome === 'repaired' && r.image_url ? (
                    <a href={r.image_url} target="_blank" rel="noreferrer" className="underline decoration-white/30 hover:decoration-white">
                      {r.source}
                    </a>
                  ) : (r.source || r.error || '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatBox({ label, value, tone = 'ok', icon = null, testid }) {
  const toneCls = {
    ok: 'border-white/10 bg-white/5 text-white',
    warning: 'border-amber-500/30 bg-amber-500/5 text-amber-100',
    danger: 'border-red-500/30 bg-red-500/5 text-red-100',
  }[tone] || 'border-white/10 bg-white/5 text-white';
  return (
    <div className={`rounded-lg border p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-xs opacity-70 flex items-center gap-1">{icon}{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
